import React, { useEffect, useState, useContext } from 'react'
import SchematicSelector from './schematicSelector'
import { lithologyPatterns } from './utils/utils'
import { DashboardContext } from 'live/context/dashboard'
import styled from 'styled-components'
import theme from 'live/services/theme'

const ValidationMessages = styled.div`
    p {
        color: var(--text-color);
    }
`

const checkBoolean = (value: any): boolean =>
    typeof value === 'boolean' || typeof value === 'undefined' || value === null
const checkString = (value: any): boolean => typeof value === 'string' || typeof value === 'undefined' || value === null
const checkPositiveNumberOrnull = (value: any): boolean =>
    checkPositiveNumber(value) || typeof value === 'undefined' || value === null
const checkNumberOrNull = (value: any): boolean =>
    typeof value === 'number' || typeof value === 'undefined' || value === null
const checkArrayWith2ElementsOrNull = (value: any): boolean =>
    (Array.isArray(value) && (value.length === 2 || value.length === 0)) ||
    typeof value === 'undefined' ||
    value === null

const checkPositiveNumber = (value: any): boolean => typeof value === 'number' && value >= 0

const checkCasingShoe = (value: any): boolean =>
    (typeof value === 'object' &&
        ((Object.keys(value).length === 2 &&
            checkPositiveNumberOrnull(value.ECD) &&
            checkPositiveNumberOrnull(value.depth)) ||
            Object.keys(value).length === 0)) ||
    typeof value === 'undefined' ||
    value === null

const checkLithology = (property): { validated: boolean; string?: any } => {
    const res = { validated: true }
    if (typeof property === 'object') {
        property.forEach((e, i) => {
            Object.keys(e).every(f => {
                if (
                    f !== 'depth' &&
                    f !== 'timestamp' &&
                    lithologyPatterns.indexOf(f) === -1 &&
                    (e[f] >= 0 || e[f] <= 100)
                ) {
                    res.validated = false
                    res[f] = e[f]

                    return false
                }

                return true
            })
            if (!res.validated) return false
        })
    } else if (typeof property !== null || typeof property !== undefined) {
        res.validated = false
    }

    return res
}

const checkSectionsHC = (value: any): boolean => {
    let res = true
    if (Array.isArray(value) || value === null || typeof value === 'undefined') {
        value?.every(e => {
            if (
                !(
                    Object.keys(e).length >= 2 &&
                    (checkPositiveNumber(e.bit_diameter) || checkPositiveNumber(e.innerDiameter)) &&
                    (checkPositiveNumber(e.endDepth) || checkPositiveNumber(e.md_casing_shoe)) &&
                    checkPositiveNumberOrnull(e.startDepth) &&
                    checkPositiveNumberOrnull(e.casing_begin) &&
                    checkString(e.uom) &&
                    checkPositiveNumberOrnull(e.depth)
                )
            ) {
                res = false

                return false
            }
        })
    } else {
        res = false
    }

    return res
}

const checkSections = (value: any): boolean =>
    checkSectionsHC(value) ||
    typeof value === 'undefined' ||
    value === null ||
    (Array.isArray(value) && value.length === 0)

const booleanMessage = (property: object, key: number): JSX.Element => (
    <p key={key}>
        The property <b>{Object.keys(property)[0]}</b> can not have a type different of boolean or null. The type found
        is {typeof Object.values(property)[0]}
    </p>
)

const stringMessage = (property: object, key: number): JSX.Element => (
    <p key={key}>
        The property <b>{Object.keys(property)[0]}</b> can not have a type different of string or null. The type found
        is {typeof Object.values(property)[0]}
    </p>
)

const numberOrNullMessage = (property: object, key: number): JSX.Element => (
    <p key={key}>
        The property <b>{Object.keys(property)[0]}</b> can not have a type different of number or null. The type found
        is {typeof Object.values(property)[0]}
    </p>
)

const positiveNumberMessage = (property: object, key: number): JSX.Element => (
    <p key={key}>
        The property <b>{Object.keys(property)[0]}</b> is mandatory and can not have a type different of number and must
        be a positive number. The type found is {typeof Object.values(property)[0]}
    </p>
)

const positiveNumberOrNullMessage = (property: object, key: number): JSX.Element => (
    <p key={key}>
        The property <b>{Object.keys(property)[0]}</b> can not have a type different of number or null, and must be a
        positive number. The type found is {typeof Object.values(property)[0]}
    </p>
)

const arrayWith2ElementsMessage = (key: number): JSX.Element => (
    <p key={key}>
        The property <b>Range</b> can not have a type different of array (with 2 elemets) or null, and the values must
        be positive numbers.
    </p>
)

const casingShoeErrorMessage = (property: object, key: number): JSX.Element => (
    <p key={key}>
        The property <b>casingShoe</b> can not have a type different of object or null, and must have the properties ECD
        and depth. The type found is {typeof property}
    </p>
)

const sectionsErrorMessage = (key: number): JSX.Element => (
    <p key={key}>
        The property <b>sections</b> must be an object containing the properties bit_diameter/innerDiameter (positive
        numerical or null) and endDepth/md_casing_shoe (positive numerical or null), and possibly having the properties
        casing_begin/startDepth (positive numerical or null) and uom (string or null).
    </p>
)

const sectionsHCErrorMessage = (key: number): JSX.Element => (
    <p key={key}>
        When the schematicType is set to 'holecleaning' the property <b>sections</b> must be null or an array of objects
        containing the properties bit_diameter/innerDiameter (positive numerical value) and endDepth/md_casing_shoe
        (positive numerical value), and possibly having the properties casing_begin/startDepth (positive numerical or
        null) and uom (string or null).
    </p>
)

const lithologyErrorMessage = (property: object, key: number): JSX.Element => (
    <p key={key}>
        On the 2nd layer of the query are passed the <b>lithology</b> values, besides the deep property (a positive
        numerical value), the other properties must be values between 0 and 100, and have names which follow patterns in
        Live. The following values do not follow that behavior:{' '}
        {Object.keys(property).map(e => (e === 'validated' ? '' : '' + e + ': ' + property[e] + ' '))}
    </p>
)

export default function DataValidation({
    casingShoe,
    holeDepth,
    drillstringVelocity,
    bitDepth,
    prevBitDepth,
    uom,
    uomVelocity,
    lithology,
    schematicType,
    sections,
    range,
    allowZoom,
    heightOfOGMCartesian,
    showDrillBit,
    hideHoleDepth,
    paddingTop,
    paddingBottom,
    width,
    height,
    bottomComponent
}: SchematicValues): JSX.Element {
    const context = useContext(DashboardContext)
    const [errorMsgs, setErrorMsgs] = useState([])

    useEffect(() => {
        const res = []

        if (!checkPositiveNumberOrnull(bitDepth)) res.push(positiveNumberOrNullMessage({ bitDepth }, res.length))
        if (!checkPositiveNumberOrnull(prevBitDepth)) {
            res.push(positiveNumberOrNullMessage({ prevBitDepth }, res.length))
        }
        if (!checkPositiveNumber(holeDepth)) res.push(positiveNumberMessage({ holeDepth }, res.length))
        if (!checkNumberOrNull(drillstringVelocity)) res.push(numberOrNullMessage({ drillstringVelocity }, res.length))
        if (!checkString(uom)) res.push(stringMessage({ uom }, res.length))
        if (!checkString(uomVelocity)) res.push(stringMessage({ uomVelocity }, res.length))
        if (!checkString(schematicType)) res.push(stringMessage({ schematicType }, res.length))
        if (!checkArrayWith2ElementsOrNull(range)) res.push(arrayWith2ElementsMessage(res.length))
        if (!checkCasingShoe(casingShoe)) res.push(casingShoeErrorMessage(casingShoe, res.length))

        if (schematicType === 'holecleaning') {
            if (!checkSectionsHC(sections)) res.push(sectionsHCErrorMessage(res.length))
        } else {
            if (!checkSections(sections)) res.push(sectionsErrorMessage(res.length))
        }

        if (!checkBoolean(allowZoom)) res.push(booleanMessage({ allowZoom }, res.length))
        if (!checkBoolean(heightOfOGMCartesian)) res.push(booleanMessage({ heightOfOGMCartesian }, res.length))
        const resCheckinLithology = checkLithology(lithology)
        if (!resCheckinLithology.validated) res.push(lithologyErrorMessage(resCheckinLithology, res.length))
        if (!checkBoolean(showDrillBit)) res.push(booleanMessage({ showDrillBit }, res.length))

        setErrorMsgs(res)
    }, [holeDepth, prevBitDepth, bitDepth, drillstringVelocity, uom, uomVelocity, allowZoom, heightOfOGMCartesian])

    const sectionsWithBitHoleDepth = React.useMemo(
        () =>
            [
                ...sections,
                {
                    md_casing_shoe: bitDepth,
                    bit_diameter: 0,
                    type: 'bitDepth'
                },
                {
                    md_casing_shoe: holeDepth,
                    bit_diameter: 0,
                    type: 'holeDepth'
                }
            ].sort((a, b) => a.md_casing_shoe - b.md_casing_shoe),
        [sections, bitDepth, holeDepth]
    )

    if (errorMsgs.some(e => e !== null)) {
        return <ValidationMessages theme={context.theme}>{errorMsgs.map(e => e)}</ValidationMessages>
    }

    return (
        <SchematicSelector
            casingShoe={casingShoe}
            holeDepth={holeDepth}
            drillstringVelocity={drillstringVelocity}
            bitDepth={bitDepth}
            prevBitDepth={prevBitDepth}
            uom={uom}
            uomVelocity={uomVelocity}
            lithology={lithology}
            schematicType={schematicType}
            sections={sectionsWithBitHoleDepth}
            range={range}
            allowZoom={allowZoom}
            heightOfOGMCartesian={heightOfOGMCartesian}
            showDrillBit={showDrillBit}
            hideHoleDepth={hideHoleDepth}
            paddingTop={paddingTop}
            paddingBottom={paddingBottom}
            width={width}
            height={height}
            bottomComponent={bottomComponent}
        />
    )
}
